import type { Category } from '../types';

export interface CategoryNode extends Category {
  children: CategoryNode[];
}

const DEFAULT_CATEGORIES: Omit<Category, 'id'>[] = [
  { name: 'Servicios', parentId: null, color: '#2196F3' },
  { name: 'Luz', parentId: 'servicios', color: '#2196F3' },
  { name: 'Agua', parentId: 'servicios', color: '#2196F3' },
  { name: 'Internet', parentId: 'servicios', color: '#2196F3' },
  { name: 'Teléfono', parentId: 'servicios', color: '#2196F3' },
  { name: 'Impuestos', parentId: 'servicios', color: '#2196F3' },
  { name: 'Otros servicios', parentId: 'servicios', color: '#2196F3' },

  { name: 'Suscripciones', parentId: null, color: '#9C27B0' },
  { name: 'Streaming', parentId: 'suscripciones', color: '#9C27B0' },
  { name: 'Software', parentId: 'suscripciones', color: '#9C27B0' },
  { name: 'Trabajo', parentId: 'suscripciones', color: '#9C27B0' },
  { name: 'Otras suscripciones', parentId: 'suscripciones', color: '#9C27B0' },

  { name: 'Alimentación', parentId: null, color: '#FF9800' },
  { name: 'Supermercado', parentId: 'alimentacion', color: '#FF9800' },
  { name: 'Hogar y Limpieza', parentId: 'alimentacion', color: '#FF9800' },
  { name: 'Restaurantes', parentId: 'alimentacion', color: '#FF9800' },
  { name: 'Delivery', parentId: 'alimentacion', color: '#FF9800' },

  { name: 'Transporte', parentId: null, color: '#607D8B' },
  { name: 'Gasolina', parentId: 'transporte', color: '#607D8B' },
  { name: 'Uber/Taxi', parentId: 'transporte', color: '#607D8B' },
  { name: 'Mantenimiento', parentId: 'transporte', color: '#607D8B' },

  { name: 'Salud', parentId: null, color: '#E91E63' },
  { name: 'Médico', parentId: 'salud', color: '#E91E63' },
  { name: 'Farmacia', parentId: 'salud', color: '#E91E63' },
  { name: 'Gimnasio', parentId: 'salud', color: '#E91E63' },
  { name: 'Suplementos', parentId: 'salud', color: '#E91E63' },

  { name: 'Entretenimiento', parentId: null, color: '#00BCD4' },
  { name: 'Cine', parentId: 'entretenimiento', color: '#00BCD4' },
  { name: 'Videojuegos', parentId: 'entretenimiento', color: '#00BCD4' },
  { name: 'Libros', parentId: 'entretenimiento', color: '#00BCD4' },
  { name: 'Juguetes', parentId: 'entretenimiento', color: '#00BCD4' },
  { name: 'Salidas', parentId: 'entretenimiento', color: '#00BCD4' },
  {
    name: 'Otros entretenimiento',
    parentId: 'entretenimiento',
    color: '#00BCD4',
  },

  { name: 'Compras', parentId: null, color: '#795548' },
  { name: 'Ropa', parentId: 'compras', color: '#795548' },
  { name: 'Electrónica', parentId: 'compras', color: '#795548' },
  { name: 'Hogar', parentId: 'compras', color: '#795548' },
  { name: 'Regalos', parentId: 'compras', color: '#795548' },
  { name: 'Otras compras', parentId: 'compras', color: '#795548' },

  { name: 'Educación', parentId: null, color: '#3F51B5' },
  { name: 'Colegiaturas', parentId: 'educacion', color: '#3F51B5' },

  { name: 'Mascotas', parentId: null, color: '#8BC34A' },
  { name: 'Veterinario', parentId: 'mascotas', color: '#8BC34A' },
  { name: 'Tienda de mascotas', parentId: 'mascotas', color: '#8BC34A' },
  { name: 'Comida de mascotas', parentId: 'mascotas', color: '#8BC34A' },

  { name: 'Seguros', parentId: null, color: '#FF5722' },
  { name: 'Auto', parentId: 'seguros', color: '#FF5722' },
  { name: 'Gastos médicos', parentId: 'seguros', color: '#FF5722' },

  { name: 'Transferencias', parentId: null, color: '#78909C' },

  { name: 'Sin categoría', parentId: null, color: '#9E9E9E' },
];

export function generateId(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function generateUniqueId(categories: Category[], baseName: string): string {
  const baseId = generateId(baseName);
  const existingIds = new Set(categories.map((cat) => cat.id));

  if (!existingIds.has(baseId)) {
    return baseId;
  }

  let counter = 1;
  while (existingIds.has(`${baseId}-${counter}`)) {
    counter++;
  }
  return `${baseId}-${counter}`;
}

export function getDefaultCategories(): Category[] {
  return DEFAULT_CATEGORIES.map((cat) => ({
    ...cat,
    id: generateId(cat.name),
  }));
}

export function buildCategoryTree(categories: Category[]): CategoryNode[] {
  const categoryMap = new Map<string, CategoryNode>();
  const roots: CategoryNode[] = [];

  categories.forEach((cat) => {
    categoryMap.set(cat.id, { ...cat, children: [] });
  });

  categories.forEach((cat) => {
    const node = categoryMap.get(cat.id)!;
    if (cat.parentId === null) {
      roots.push(node);
    } else {
      const parent = categoryMap.get(cat.parentId);
      if (parent) {
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    }
  });

  return roots;
}

export function findCategoryById(
  categories: Category[],
  id: string
): Category | undefined {
  return categories.find((cat) => cat.id === id);
}

export function getCategoryWithAncestors(
  categories: Category[],
  id: string
): Category[] {
  const result: Category[] = [];
  let current = findCategoryById(categories, id);

  while (current) {
    result.unshift(current);
    current = current.parentId
      ? findCategoryById(categories, current.parentId)
      : undefined;
  }

  return result;
}

export function getCategoryDescendants(
  categories: Category[],
  id: string
): Category[] {
  const descendants: Category[] = [];
  const queue = categories.filter((cat) => cat.parentId === id);

  while (queue.length > 0) {
    const current = queue.shift()!;
    descendants.push(current);
    const children = categories.filter((cat) => cat.parentId === current.id);
    queue.push(...children);
  }

  return descendants;
}

export function getRootCategories(categories: Category[]): Category[] {
  return categories.filter((cat) => cat.parentId === null);
}

export function getChildCategories(
  categories: Category[],
  parentId: string
): Category[] {
  return categories.filter((cat) => cat.parentId === parentId);
}

export function createCategory(
  categories: Category[],
  data: Omit<Category, 'id'>
): Category[] {
  const newCategory: Category = {
    ...data,
    id: generateUniqueId(categories, data.name),
  };
  return [...categories, newCategory];
}

export function updateCategory(
  categories: Category[],
  id: string,
  updates: Partial<Omit<Category, 'id'>>
): Category[] {
  return categories.map((cat) =>
    cat.id === id ? { ...cat, ...updates } : cat
  );
}

export function deleteCategory(
  categories: Category[],
  id: string,
  reassignTo: string | null = null
): Category[] {
  const descendants = getCategoryDescendants(categories, id);
  const idsToDelete = new Set([id, ...descendants.map((d) => d.id)]);

  return categories
    .filter((cat) => !idsToDelete.has(cat.id))
    .map((cat) =>
      cat.parentId && idsToDelete.has(cat.parentId)
        ? { ...cat, parentId: reassignTo }
        : cat
    );
}

export function isLeafCategory(
  categories: Category[],
  categoryId: string
): boolean {
  return !categories.some((cat) => cat.parentId === categoryId);
}

export function getLeafCategories(categories: Category[]): Category[] {
  const parentIds = new Set(
    categories.filter((cat) => cat.parentId !== null).map((cat) => cat.parentId)
  );
  return categories.filter((cat) => !parentIds.has(cat.id));
}

export interface FlattenedCategory {
  category: CategoryNode;
  depth: number;
}

export function flattenCategoryTree(
  nodes: CategoryNode[],
  depth = 0
): FlattenedCategory[] {
  const result: FlattenedCategory[] = [];
  for (const node of nodes) {
    result.push({ category: node, depth });
    if (node.children.length > 0) {
      result.push(...flattenCategoryTree(node.children, depth + 1));
    }
  }
  return result;
}

export function getTargetCategoryId(
  categoryId: string | null,
  categories: { id: string; parentId: string | null }[],
  selectedCategoryIds: Set<string>,
  categoryMap?: Map<string, { id: string; parentId: string | null }>
): string | null {
  if (!categoryId) return null;

  if (selectedCategoryIds.has(categoryId)) {
    return categoryId;
  }

  const map = categoryMap ?? new Map(categories.map((c) => [c.id, c]));

  let current = categoryId;
  while (current) {
    const category = map.get(current);
    if (!category) return null;

    if (selectedCategoryIds.has(category.id)) {
      return category.id;
    }

    if (category.parentId === null) {
      return selectedCategoryIds.size === 0 ? category.id : null;
    }
    current = category.parentId;
  }
  return null;
}
